// Staff Portal — Create Class.
//
// Renders the exact admin Create-Class form (shared <ClassForm />),
// with the Staff Member field locked to the authenticated staff and
// the Subjects & Grades catalogue narrowed to the caller's own
// teaching assignments. All authorization is server-side:
//
//   • POST /api/staff-portal/classes (StaffPortalController::storeClass)
//     OVERWRITES staff_id with the authenticated staff id, validates
//     every submitted select_grade_subject_ids[] against the caller's
//     staffTeachesPairs scope, and strips billing fields unless the
//     caller holds Invoice_Create. Even if this UI is bypassed via
//     curl or devtools, the endpoint enforces the same contract.
//
//   • The Staff Member picker is disabled — the underlying value is
//     seeded from /staff-portal/my-teaching-assignments (which itself
//     is auth-derived) and never editable client-side. If a client
//     did forge a different staff_id, the server would drop it
//     before the transaction ran.
//
// Visual parity with the admin form is intentional — a teacher who
// has seen /assign/classes/create should recognise every section on
// /staff-portal/classes/create. The only differences are: (a) the
// Staff Member field is locked; (b) the Subjects & Grades catalogue
// only contains the caller's assignments; (c) the Location picker
// falls back to Online-first when the caller cannot list locations.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Skeleton, Stack, Typography, Button, Alert } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNotifier } from "../../Core/Notifier";
import {
  useGetMyTeachingAssignmentsQuery,
  useStoreMyClassMutation,
  useGetallLocationQuery,
  useGetallSubjectQuery,
} from "../../Api/attoDeskApi";
import ClassForm, { requiresLocation, TimeConflict } from "../Classes/ClassForm";
import BackDrop from "../../Components/Loader/BackDrop";
import { ClassType, TeachMood } from "../../Core/Enum/enum";
import type {
  CustomerOption,
  IClassCreate,
  IGrade,
  IStudentLocation,
  ISubject,
  ISubjectGradePerent,
} from "../../Api/Interface/api.interface";
import { isPeriodRangeSubmittable, rangeHasSelectedWeekday } from "../Classes/Create/periodValidation";

// Response shape from /staff-portal/my-teaching-assignments — subject-first,
// which we flatten to the grade-first IGrade[] shape ClassForm expects.
type MyAssignment = {
  subject_id: number;
  subject_name: string;
  grades: Array<{ grade_id: number; grade_name: string; subject_grade_id: number }>;
};

const StaffPortalCreateClass: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const {
    data: assignmentsResp,
    isLoading: assignmentsLoading,
    isFetching: assignmentsFetching,
    isError: assignmentsError,
  } = useGetMyTeachingAssignmentsQuery();

  // Best-effort locations — a staff without Location_View gets 403 and
  // we degrade gracefully to an empty options list. In-Person mode
  // still validates at the server; the UI just cannot offer a choice.
  const { data: locationData, isLoading: locationIsLoading } = useGetallLocationQuery({
    searchText: "",
  });

  // Subject dictionary — subject_id → subject_name — is what ClassForm
  // renders each chip with. We can derive this locally from
  // assignmentsResp, so a Subjects_View permission is not required.
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });

  const [storeMyClass, { isLoading: saving }] = useStoreMyClassMutation();

  const payload: any = assignmentsResp?.data || null;
  const staff = payload?.staff as { id: number; name: string } | null | undefined;
  const isAdmin = !!payload?.is_admin;
  const assignments: MyAssignment[] = payload?.assignments || [];

  // ── Flatten subject-first payload → grade-first IGrade[] ──
  // ClassForm expects rows of { grade, selected_sub_array } where each
  // sub carries { id: subject_grade_id, subject_id, is_assigned_to_staff }.
  // Every pair returned by the endpoint is teachable by definition, so
  // every sub row is marked assigned.
  const subjectGRadeDataList: IGrade[] = useMemo(() => {
    const gradeMap = new Map<
      number,
      { id: number; grade: string; selected_sub_array: ISubjectGradePerent[] }
    >();
    assignments.forEach((subj) => {
      subj.grades.forEach((g) => {
        const key = g.grade_id;
        if (!gradeMap.has(key)) {
          gradeMap.set(key, { id: g.grade_id, grade: g.grade_name, selected_sub_array: [] });
        }
        gradeMap.get(key)!.selected_sub_array.push({
          id: g.subject_grade_id,
          subject_id: subj.subject_id,
          grade_id: g.grade_id,
          is_assigned_to_staff: true,
        });
      });
    });
    return Array.from(gradeMap.values())
      .sort((a, b) => a.grade.localeCompare(b.grade, undefined, { numeric: true }))
      .map((g) => ({
        ...g,
        subject_ids: g.selected_sub_array.map((s) => s.subject_id),
        order_number: 0,
      })) as IGrade[];
  }, [assignments]);

  const subjectDataList: ISubject[] = Array.isArray(subjectData?.data)
    ? (subjectData?.data as ISubject[])
    : [];

  // Prefer the global subject catalogue for names when Subjects_View is
  // granted; otherwise fall back to the names embedded in
  // /my-teaching-assignments (subject_name). Every subject_id the form
  // will render is present in the assignments payload, so no lookup misses.
  const subjectMap = useMemo(() => {
    const map = new Map<number, string>();
    subjectDataList.forEach((s) => {
      map.set(s.id, s.subject_name);
    });
    assignments.forEach((a) => {
      if (!map.has(a.subject_id)) map.set(a.subject_id, a.subject_name);
    });
    return map;
  }, [subjectDataList, assignments]);

  const locationList: IStudentLocation[] = Array.isArray(locationData?.data)
    ? (locationData?.data as IStudentLocation[])
    : [];
  const locationOptions: CustomerOption[] = useMemo(
    () =>
      locationList.map((loc) => ({
        label: loc.location_name || loc.location_address,
        value: loc.id,
      })),
    [locationList],
  );

  // Staff Member picker — the ONLY option is the acting staff, seeded
  // from the auth-derived endpoint. Passing a single-entry list means
  // the Autocomplete renders the correct label without any staff-list
  // query; the ClassForm's `staffLocked` prop then disables interaction.
  const staffPerentOptions: CustomerOption[] = useMemo(() => {
    if (!staff) return [];
    return [{ label: staff.name, value: staff.id }];
  }, [staff]);

  // ── Local form state (matches admin Index.tsx patterns) ──
  const [selectedSubject, setSelectedSubject] = useState<number[]>([]);
  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
  const [programType, setProgramType] = useState<"regular" | "special">("regular");

  const seededStaffRef = useRef(false);

  const formik = useFormik<IClassCreate>({
    initialValues: {
      staff_id: "",
      location_id: "",
      class_mode: "",
      class_type: "",
      number_of_student: null,
      select_grade_subject_ids: [],
      period_based_from_date: null,
      period_based_to_date: null,
      notes: "",
      selected_days: [],
      startTime: "",
      endTime: "",
      default_fee: null,
      default_tax_status: "exempt",
      default_fee_effective_from: dayjs().format("YYYY-MM-DD"),
    },
    enableReinitialize: false,

    validate: (values) => {
      const errors: { [key: string]: string } = {};
      const today = dayjs().startOf("day");
      const isPeriodBased = values.class_type === ClassType["Period-Based"];

      if (isPeriodBased && !values.period_based_from_date) {
        errors.period_based_from_date = "Start date is required";
      }
      if (isPeriodBased && !values.period_based_to_date) {
        errors.period_based_to_date = "End date is required";
      }
      if (values.period_based_from_date) {
        const fromDate = values.period_based_from_date.startOf("day");
        if (fromDate.isBefore(today)) {
          errors.period_based_from_date = "From date cannot be in the past";
        }
      }
      if (values.period_based_from_date && values.period_based_to_date) {
        const fromDate = values.period_based_from_date.startOf("day");
        const toDate = values.period_based_to_date.startOf("day");
        if (toDate.isBefore(fromDate)) {
          errors.period_based_to_date = "To date must be the same as or after From date";
        } else if (
          isPeriodBased &&
          selectedWeekDays.length > 0 &&
          !rangeHasSelectedWeekday(fromDate, toDate, selectedWeekDays)
        ) {
          errors.period_based_to_date = "None of the selected days fall within this date range";
        }
      }

      const isValidTime = (t: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(t);
      const toMinutes = (time: string) => {
        const [h, m] = time.split(":").map(Number);
        return h * 60 + m;
      };
      if (!values.startTime || !isValidTime(values.startTime)) {
        errors.startTime = "Start time is required (HH:mm)";
      }
      if (!values.endTime || !isValidTime(values.endTime)) {
        errors.endTime = "End time is required (HH:mm)";
      }
      if (
        values.startTime &&
        values.endTime &&
        isValidTime(values.startTime) &&
        isValidTime(values.endTime)
      ) {
        const startMin = toMinutes(values.startTime);
        const endMin = toMinutes(values.endTime);
        if (endMin <= startMin) errors.endTime = "End time must be after start time";
      }

      return errors;
    },

    onSubmit: async (values) => {
      try {
        // staff_id is intentionally NOT sent — the SP controller derives
        // it from auth and overwrites any client-supplied value. Keeping
        // the payload minimal makes that contract explicit.
        const body: any = {
          location_id: values.location_id || undefined,
          class_mode: values.class_mode,
          class_type: values.class_type,
          program_type: programType,
          number_of_student: values.number_of_student,
          select_grade_subject_ids: selectedSubject,
          notes: values.notes,
          selected_days: selectedWeekDays,
          period_based_from_date: values.period_based_from_date
            ? values.period_based_from_date.format("YYYY-MM-DD")
            : null,
          period_based_to_date: values.period_based_to_date
            ? values.period_based_to_date.format("YYYY-MM-DD")
            : null,
          startTime: values.startTime,
          endTime: values.endTime,
          // Billing fields are stripped server-side unless the caller holds
          // Invoice_Create. Include them; the endpoint is the gate.
          default_fee: values.default_fee ?? null,
          default_tax_status: values.default_tax_status ?? "exempt",
          default_fee_effective_from:
            values.default_fee_effective_from ?? dayjs().format("YYYY-MM-DD"),
        };
        const r: any = await storeMyClass(body).unwrap();
        if (!r?.status) {
          showErrorMessage(r?.message || "Unable to create class.");
          return;
        }
        showMessage(r?.message || "Class created successfully.");
        navigate("/staff-portal");
      } catch (err: any) {
        showErrorMessage(err?.data?.message || "Unable to create class.");
      }
    },
  });

  // Seed the locked staff id into formik once the endpoint resolves.
  // Reset preserves this because initialValues never held the id — we
  // re-seed it explicitly on every reset via the onReset callback.
  useEffect(() => {
    if (!staff || seededStaffRef.current) return;
    seededStaffRef.current = true;
    formik.setFieldValue("staff_id", String(staff.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff]);

  const handleClassModeChange = (value: string) => {
    formik.setFieldValue("class_mode", value);
    if (value === TeachMood.Online || value === "") {
      formik.setFieldValue("location_id", "");
    }
  };

  const handleClassTypeChange = (value: string) => {
    formik.setFieldValue(
      "class_type",
      formik.values.class_type === value ? "" : value,
    );
  };

  const handleSubjectToggle = (id: number) => {
    setSelectedSubject((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleWeekDayToggle = (day: string) => {
    setSelectedWeekDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  // Reset — preserves the locked staff identity, clears everything else.
  const handleReset = () => {
    formik.resetForm();
    setSelectedSubject([]);
    setSelectedWeekDays([]);
    setProgramType("regular");
    // Re-seed the staff id after resetForm() cleared it.
    if (staff) formik.setFieldValue("staff_id", String(staff.id));
  };

  // Conflict detection is admin-only (needs the staff schedule query
  // that is Classes_View gated). SP shows no conflict warnings — the
  // backend rejects genuine overlaps with a 422 on submit.
  const timeConflicts: TimeConflict[] = [];
  const hasConflicts = false;

  const timeOrderError = useMemo(() => {
    if (!formik.values.startTime || !formik.values.endTime) return "";
    const toMin = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    const s = toMin(formik.values.startTime);
    const e = toMin(formik.values.endTime);
    if (Number.isNaN(s) || Number.isNaN(e)) return "";
    if (e <= s) return "End time must be after start time";
    return "";
  }, [formik.values.startTime, formik.values.endTime]);

  const formValid = useMemo(() => {
    if (!formik.values.staff_id || !formik.values.startTime || !formik.values.endTime) return false;
    if (!formik.values.class_mode) return false;
    if (requiresLocation(formik.values.class_mode) && !formik.values.location_id) return false;
    if (selectedWeekDays.length === 0) return false;
    if (selectedSubject.length === 0) return false;
    if (timeOrderError) return false;
    if (
      formik.values.class_type === ClassType["Period-Based"] &&
      !isPeriodRangeSubmittable(
        formik.values.period_based_from_date,
        formik.values.period_based_to_date,
        selectedWeekDays,
      )
    ) {
      return false;
    }
    return true;
  }, [formik, selectedSubject, selectedWeekDays, timeOrderError]);

  const pageIsLoading = assignmentsLoading || assignmentsFetching;

  // Admin bypass — an admin has no fixed teaching scope on this endpoint.
  // Nudge them to the admin catalogue rather than showing an empty form.
  if (isAdmin) {
    return (
      <Box sx={{ pb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/staff-portal")}
          sx={{ textTransform: "none", fontWeight: 700, mb: 2 }}
        >
          Back to Staff Portal
        </Button>
        <Alert severity="info">
          You have system-admin scope. Please use the admin{" "}
          <Box component="a" href="/assign/classes/create" sx={{ fontWeight: 800 }}>
            Create Class
          </Box>{" "}
          page to select any teacher and any subject.
        </Alert>
      </Box>
    );
  }

  // While the auth-derived identity is loading, show skeletons in each
  // section — never flash the "couldn't load" or "no assignments" copy
  // during a legitimate load. (Memory: verify consumer before fixing
  // producer — these strings must only fire on a resolved response.)
  if (pageIsLoading) {
    return (
      <Box sx={{ pb: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Create New Class</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Loading your teaching profile…
            </Typography>
          </Box>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      </Box>
    );
  }

  // Resolved with no staff record — surface the same warning shape the
  // rest of the Staff Portal uses, but only AFTER load resolves.
  if (assignmentsError || !staff) {
    return (
      <Box sx={{ pb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/staff-portal")}
          sx={{ textTransform: "none", fontWeight: 700, mb: 2 }}
        >
          Back to Staff Portal
        </Button>
        <Alert severity="warning">
          Couldn't load your staff profile. Please refresh, and if this persists contact an administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/staff-portal")}
        sx={{ textTransform: "none", fontWeight: 700, mb: 2 }}
      >
        Back to Staff Portal
      </Button>
      <ClassForm
        mode="create"
        formik={formik}
        formValid={formValid}
        submitting={saving}
        heading="Create New Class"
        subheading="Set up a new class from your teaching assignments. Your staff identity is locked."
        studentPerentOptions={staffPerentOptions}
        filteredStaffOptions={staffPerentOptions}
        studentPerentIsLoading={false}
        onStaffChange={() => {
          // No-op — the field is locked; the underlying value is
          // auth-derived and never accepts a client-side change.
        }}
        onStaffSearchInput={() => {
          /* locked — no search */
        }}
        staffLocked
        locationOptions={locationOptions}
        filteredLocationOptions={locationOptions}
        staffLocationIds={[]}
        isLocationInvalid={false}
        locationIsLoading={locationIsLoading}
        onClassModeChange={handleClassModeChange}
        onClassTypeChange={handleClassTypeChange}
        programType={programType}
        onProgramTypeChange={setProgramType}
        subjectGRadeDataList={subjectGRadeDataList}
        subjectMap={subjectMap}
        subjectIsLoading={false}
        subjectGradeIsLoading={false}
        isSubjectError={false}
        selectedSubject={selectedSubject}
        onSubjectToggle={handleSubjectToggle}
        selectedWeekDays={selectedWeekDays}
        onWeekDayToggle={handleWeekDayToggle}
        timeConflicts={timeConflicts}
        hasConflicts={hasConflicts}
        timeOrderError={timeOrderError}
        submitLabel="Create Class"
        onReset={handleReset}
      />
      {saving && <BackDrop open={saving} />}
    </Box>
  );
};

export default StaffPortalCreateClass;
